import React, { useRef, useState, useEffect } from 'react'
import classNames from 'classnames'
import {
  Form as RBForm,
  Row,
  Col,
  Container,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap'
import Imgix from 'react-imgix'
import axios from 'axios'
import {
  Button,
  useErrorService,
  useNotificationService,
  ABSpinner,
  CloseIcon,
} from '@abroad/components'
import { InformationIcon } from '../components/assetsComponents'
import { useResolutionService } from '@abroad/components'
import { Formik } from 'formik'
import { useParams, useHistory } from 'react-router'
import { InputField } from '../components/form'
import { PageHeader } from '../components'
import API from '../utils/API'
import { string, number, object } from 'yup'
import ImageCropModal from '../components/common/ImageCropModal'

const validationSchema = object().shape({
  title: string().required('Required'),
  url: string()
    .matches(
      /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
      'Invalid URL',
    )
    .required('Required'),
  description: string().required('Required'),
  timeInMinute: number()
    .typeError('Time must be a number')
    .min(1, 'Time must be greater then zero')
    .required('Required'),
})

const VideoDetails = ({ video: propVideo, addVideo }) => {
  const { videoId } = useParams()
  const history = useHistory()
  const isMDScreen = useResolutionService()
  const Notification = useNotificationService()
  const [isDeleting, setIsDeleting] = useState(false)
  const [image, setImage] = useState(null)
  const [filename, setFilename] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const [imageUpload, setImageUpload] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [video, setVideo] = useState(propVideo || null)
  const [isLoading, setIsLoading] = useState(true)
  const [imageChanged, setImageChanged] = useState(false)
  const [showImageCropModal, setShowImageCropModal] = useState(false)
  const [selectedImageFile, setSelectedImageFile] = useState(null)
  const [isDraggingImage, setIsDraggingImage] = useState(false)
  const imageRef = useRef(null)
  const Error = useErrorService()
  const uploadInProgress = useRef(false)
  const inputResetRef = useRef(null)
  // Add a ref to store the Formik setFieldValue function
  const setFieldValueRef = useRef(null)

  // Fetch video details if editing
  useEffect(() => {
    if (videoId && videoId !== 'create-new') {
      const fetchVideo = async () => {
        try {
          setIsLoading(true)
          const { data } = await API.AdminAPI.quests.getVideoDetail(videoId)
          setVideo(data)
          setFilename(data.thumbnail)
          setIsLoading(false)
        } catch (err) {
          Error.showError(err)
          setIsLoading(false)
        }
      }
      fetchVideo()
    } else {
      setVideo(null)
      setFilename(null)
      setIsLoading(false)
    }
  }, [videoId])

  const getTitle = () => {
    if (video && video.title) {
      return (
        <div className='d-flex'>
          <div
            onClick={() => history.replace('/admin/experiences-media/videos')}
            className='cursor-pointer'>
            Video
          </div>
          &nbsp;/&nbsp;
          <>{video.title}</>
        </div>
      )
    } else {
      return (
        <div className='d-flex'>
          <div
            onClick={() => history.replace('/admin/experiences-media/videos')}
            className='cursor-pointer'>
            Video
          </div>
          &nbsp;/ New
        </div>
      )
    }
  }

  const handleFileClick = () => {
    if (imageRef.current) {
      imageRef.current.value = '' // Reset before opening
      uploadInProgress.current = false // Reset upload guard
      imageRef.current.click()
    }
  }

  const validateImageFile = (file, target) => {
    const allowedFormats = ['JPG', 'JPEG', 'PNG']
    const fileExtension = file.name.split('.').pop().toUpperCase()

    if (!allowedFormats.includes(fileExtension)) {
      Notification.showNotification(
        'Filetype must be one of [JPG, JPEG, PNG]',
        'danger',
      )
      if (target) target.value = ''
      return false
    }
    return true
  }

  const openCropModal = (file, resetInput) => {
    if (!file || uploadInProgress.current) return
    if (!validateImageFile(file)) {
      if (resetInput) resetInput()
      return
    }
    inputResetRef.current = resetInput || null
    setSelectedImageFile(file)
    setShowImageCropModal(true)
  }

  const resetImageInput = () => {
    if (typeof inputResetRef.current === 'function') {
      inputResetRef.current()
    }
    inputResetRef.current = null
  }

  const handleCropModalClose = () => {
    setShowImageCropModal(false)
    setSelectedImageFile(null)
    setIsDraggingImage(false)
    resetImageInput()
    uploadInProgress.current = false
  }

  const uploadAndSetVideoImage = async (file) => {
    if (!file) return
    setShowImageCropModal(false)
    uploadInProgress.current = true
    setIsUploading(true)
    setImageUpload(true)
    setImage(URL.createObjectURL(file))
    try {
      const contentType = file.name.split('.').pop().toUpperCase()
      const { data } = await API.AdminAPI.quests.getVideoThumbnailSignedURL(
        contentType,
      )
      if (data) {
        await axios.put(data.url.url, file, {
          headers: { 'Content-Type': data.url.contentType },
        })
        setFilename(data.fileName)
        setImage(null)
        if (setFieldValueRef.current) {
          setFieldValueRef.current('thumbnail', data.fileName, true)
        }
      }
    } catch (err) {
      Error.showError(err)
      setImage(null)
    } finally {
      setImageUpload(false)
      setIsUploading(false)
      uploadInProgress.current = false
      setSelectedImageFile(null)
      setIsDraggingImage(false)
      resetImageInput()
    }
  }

  const handleImageChange = async (e) => {
    const files = e.target.files
    const target = e.target // Store reference to avoid null issues

    if (files && files.length > 0) {
      openCropModal(files[0], () => {
        if (target) target.value = ''
      })
    } else {
      // If canceled, reset input and guard
      if (target) {
        target.value = ''
      }
      uploadInProgress.current = false
    }
  }

  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingImage(true)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!isDraggingImage) setIsDraggingImage(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDraggingImage(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingImage(false)
    const files = e.dataTransfer?.files
    if (files && files.length > 0) {
      openCropModal(files[0])
      e.dataTransfer.clearData()
    }
  }

  const handleRemoveImage = () => {
    setImage(null)
    setFilename(null)
    // Clear the video's thumbnail as well
    if (video) {
      setVideo((prevVideo) => ({
        ...prevVideo,
        thumbnail: null,
      }))
    }
    if (imageRef.current) {
      imageRef.current.value = ''
    }
    // Mark form as dirty by touching a field when image is removed
    if (setFieldValueRef.current) {
      setFieldValueRef.current('thumbnail', '', true)
    }
    // Mark that image has changed
    setImageChanged(true)
    // Notification.showNotification('Image removed successfully!', 'success')
  }

  const onSubmit = async (values, actions) => {
    try {
      setIsSubmitting(true)
      const imageToUpload = filename

      // ✅ Validation for missing image
      if (!imageToUpload) {
        Notification.showNotification(
          'Please select an image to upload.',
          'danger',
        )
        setIsSubmitting(false)
        return
      }
      const reqData = {
        title: values.title,
        url: values.url,
        thumbnail: imageToUpload,
        description: values.description,
        timeInMinute: values.timeInMinute,
      }
      let data
      if (videoId && videoId !== 'create-new') {
        // Update existing video
        const res = await API.AdminAPI.quests.updateVideo(videoId, reqData)
        data = res.data
        setVideo(data)
        Notification.showNotification('Video updated successfully!', 'success')
        history.push('/admin/experiences-media/videos')
      } else {
        // Create new video
        const res = await API.AdminAPI.quests.createVideo(reqData)
        data = res.data
        addVideo && addVideo(data)
        setFilename(null)
        setImage('')
        Notification.showNotification('Video added successfully!', 'success')
        history.push('/admin/experiences-media/videos')
        actions.resetForm({
          values: {
            title: '',
            url: '',
            description: '',
            timeInMinute: '',
          },
        })
      }
      setIsSubmitting(false)
    } catch (err) {
      Error.showError(err)
      setIsSubmitting(false)
    }
  }

  const deleteVideo = async () => {
    try {
      setIsDeleting(true)
      await API.AdminAPI.quests.deleteVideo(videoId)
      setIsDeleting(false)
      Notification.showNotification('Video deleted successfully!', 'success')
      history.push('/admin/experiences-media/videos')
    } catch (e) {
      setIsDeleting(false)
      Error.showError(e)
    }
  }

  if (isLoading && videoId && videoId !== 'create-new') {
    return <ABSpinner />
  }

  return (
    <main>
      <PageHeader titleText={getTitle()} hasBackArrow={true} />
      <Container
        className='pt-2 custom-offset-lg-1 custom-col-lg-12 px-lg-0'
        fluid>
        <Formik
          initialValues={
            video
              ? {
                  title: video.title || '',
                  url: video.url || '',
                  description: video.description || '',
                  timeInMinute: video.timeInMinute || '',
                  thumbnail: video.thumbnail || '', // Add thumbnail field
                }
              : {
                  title: '',
                  url: '',
                  description: '',
                  timeInMinute: '',
                  thumbnail: '', // Add thumbnail field
                }
          }
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          enableReinitialize>
          {({ handleSubmit, dirty, isValid, setFieldValue }) => {
            // Store setFieldValue in ref so it can be accessed in handleImageChange
            setFieldValueRef.current = setFieldValue

            return (
              <RBForm onSubmit={handleSubmit}>
                <Row className='mt-5'>
                  <Col lg={6}>
                    <div className='mb-3 mt-2'>
                      <div className='d-flex justify-content-between'>
                        <label className='s8'>Video Image</label>
                      </div>
                      <div
                        onClick={handleFileClick}
                        onDragEnter={handleDragEnter}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        className='quest-header-image cursor-pointer position-relative'
                        style={{
                          aspectRatio: isMDScreen ? '2/1' : '4/2',
                          minHeight: isMDScreen ? '200px' : '150px',
                          backgroundColor: '#f8f9fa',
                          border: isDraggingImage
                            ? '2px dashed #f4b62a'
                            : '2px dashed transparent',
                          borderRadius: '10px',
                          transition: 'border-color 0.2s ease',
                        }}>
                        {/* Hover overlay for update text - only show when not uploading */}
                        {!isUploading && (
                          <div
                            className='position-absolute w-100 h-100 d-flex align-items-center justify-content-center'
                            style={{
                              backgroundColor: 'rgba(0, 0, 0, 0.6)',
                              opacity: 0,
                              transition: 'opacity 0.3s ease',
                              zIndex: 5,
                              borderRadius: '10px',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.opacity = 1
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.opacity = 0
                            }}>
                            <span className='text-saffron-700 s8 font-weight-bold'>
                              {isDraggingImage
                                ? 'Drop image to upload'
                                : filename || video?.thumbnail || image
                                ? 'Click to update'
                                : 'Upload Image'}
                            </span>
                          </div>
                        )}
                        {isUploading && (
                          <div className='text-saffron-700 uploading-text-quest'>
                            Uploading...
                          </div>
                        )}
                        {filename || video?.thumbnail ? (
                          <>
                            {/* Close icon for removing uploaded image */}
                            <div
                              className='rounded-circle quest-close-icon position-absolute top-0 right-0 p-2'
                              style={{ zIndex: 10 }}
                              onClick={(e) => {
                                e.stopPropagation()
                                e.preventDefault()
                                handleRemoveImage()
                              }}>
                              <CloseIcon
                                className='d-flex align-items-center justify-content-center'
                                fontSize='10px'
                                color='#000'
                                discardMargin={true}
                              />
                            </div>
                            <Imgix
                              imgixParams={{
                                fit: 'crop',
                                crop: 'faces,center',
                                ar: isMDScreen ? '2/1' : '4/2',
                              }}
                              src={`${
                                process.env.REACT_APP_IMG_SOURCE
                              }/videos/${filename || video?.thumbnail}`}
                              attributeConfig={{
                                src: 'data-src',
                                srcSet: 'data-srcset',
                                sizes: 'data-sizes',
                              }}
                              htmlAttributes={{
                                src: `${
                                  process.env.REACT_APP_IMG_SOURCE
                                }/videos/${filename || video?.thumbnail}?ar=${
                                  isMDScreen ? '2/1' : '4/2'
                                }&fit=crop&crop=faces,center&fill=blur&blur=300&px=16&auto=format`,
                              }}
                              width='100%'
                              height='100%'
                              alt='Selected image preview'
                              className={classNames(
                                'border-radius-10 questImage-container lazyload w-100 h-100',
                                {
                                  itenaryImage: imageUpload,
                                },
                              )}
                              style={{
                                objectFit: 'cover',
                                aspectRatio: isMDScreen ? '2/1' : '4/2',
                              }}
                            />
                          </>
                        ) : image ? (
                          <>
                            {/* Close icon for removing local image */}
                            <div
                              className='rounded-circle quest-close-icon position-absolute top-0 right-0 p-2'
                              style={{ zIndex: 10 }}
                              onClick={(e) => {
                                e.stopPropagation()
                                e.preventDefault()
                                handleRemoveImage()
                              }}>
                              <CloseIcon
                                className='d-flex align-items-center justify-content-center'
                                fontSize='10px'
                                color='#000'
                                discardMargin={true}
                              />
                            </div>
                            <img
                              src={image}
                              alt='local'
                              style={{
                                aspectRatio: isMDScreen ? '2/1' : '4/2',
                                objectFit: 'cover',
                                width: '100%',
                                height: '100%',
                              }}
                              className={classNames(
                                'border-radius-10 questImage-container lazyload',
                                {
                                  itenaryImage: imageUpload,
                                },
                              )}
                            />
                          </>
                        ) : null}
                        <RBForm.Control
                          type='file'
                          size='lg'
                          placeholder='Select File'
                          accept='image/jpg, image/jpeg, image/png'
                          onChange={handleImageChange}
                          style={{ display: 'none' }}
                          name='imageFileName'
                          sm='10'
                          ref={imageRef}
                          tabIndex={-1}
                        />
                      </div>
                      <ImageCropModal
                        show={showImageCropModal}
                        onHide={handleCropModalClose}
                        file={selectedImageFile}
                        onConfirm={uploadAndSetVideoImage}
                        isUploading={isUploading}
                        aspect={isMDScreen ? 2 / 1 : 4 / 2}
                        title='Crop Image'
                      />
                    </div>
                  </Col>
                  <Col lg={6} className='d-flex flex-column pt-4'>
                    {video && (
                      <div className='mb-3'>
                        <Button
                          variant='outline-danger'
                          className='px-4'
                          onClick={deleteVideo}
                          disabled={isDeleting}
                          type='button'>
                          {isDeleting ? 'Deleting Video' : 'Delete Video'}
                        </Button>
                      </div>
                    )}
                    <label htmlFor='title' className='s8'>
                      TITLE
                    </label>
                    <InputField
                      id='title'
                      name='title'
                      size='lg'
                      placeholder='Enter title'
                      showErrorMessage={true}
                    />
                    <InputField
                      name='url'
                      label={
                        <div className='d-flex align-items-center'>
                          Upload Video with YouTube Link
                          <OverlayTrigger
                            key='top'
                            placement='top'
                            overlay={
                              <Tooltip
                                id='tooltip-youtube-link'
                                className='font-open-sans'>
                                The URL should be in this format:
                                https://www.youtube.com/watch?v=LmC66LldZSQ.
                              </Tooltip>
                            }>
                            <InformationIcon iconClasses='align-self-center ml-2' />
                          </OverlayTrigger>
                        </div>
                      }
                      placeholder='Video Link'
                      showErrorMessage={true}
                      displayFlex={false}
                      labelProps={{ className: 's8' }}
                      inputClasses='input-typo text-dark-900'
                    />

                    <label htmlFor='timeInMinute' className='s8'>
                      Time in minutes
                    </label>
                    <InputField
                      id='timeInMinute'
                      name='timeInMinute'
                      size='lg'
                      placeholder='Enter time in minutes'
                      showErrorMessage={true}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col lg={12} className='mt-3'>
                    <label htmlFor='description' className='s8 my-2'>
                      DESCRIPTION
                    </label>
                    <InputField
                      as='textarea'
                      id='description'
                      name='description'
                      size='lg'
                      placeholder='Enter description'
                      showErrorMessage={true}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col lg={12}>
                    <div className='text-right mt-3'>
                      <Button
                        // disabled={
                        //   !(isValid && dirty) ||
                        //   (!filename && !video?.thumbnail) ||  // This is the problem!
                        //   isSubmitting
                        // }
                        disabled={
                          !(isValid && (dirty || imageChanged)) ||
                          (videoId === 'create-new' &&
                            !filename &&
                            !video?.thumbnail) ||
                          isSubmitting
                        }
                        variant='gray'
                        size='md'
                        className='line-height-normal border-radius-3 text-uppercase'
                        type='submit'>
                        {videoId && videoId !== 'create-new' ? 'UPDATE' : 'ADD'}
                      </Button>
                    </div>
                  </Col>
                </Row>
              </RBForm>
            )
          }}
        </Formik>
      </Container>
    </main>
  )
}

export default VideoDetails
